/*
 * Purpose: Workflow analytics from execution logs (not mock data)
 * Last modified: 2026-06-28 | Agent
 * Completeness: 88
 */
import { useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { Download, TrendingUp, TrendingDown, Activity, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { useLogStore } from '@/stores/logStore';
import { buildDailyAnalytics, aggregateNodePerformance } from '@/lib/executionInsights';
import { Link } from 'react-router-dom';
import { APP_ROUTES } from '@/lib/appRoutes';
import { useFlowTracingHistoryStore } from '@/stores/flowTracingHistoryStore';
import { useWorkflowStore } from '@/stores/workflowStore';
import { analyzeInlineValidation } from '@/services/inlineValidation';

export function WorkflowAnalytics() {
  const logs = useLogStore((s) => s.logs);
  const currentWorkflow = useWorkflowStore((s) => s.currentWorkflow);
  const history = useFlowTracingHistoryStore((s) =>
    currentWorkflow?.id ? s.history[currentWorkflow.id] ?? [] : []
  );

  const analyticsData = useMemo(() => buildDailyAnalytics(logs, 7), [logs]);
  const nodePerformance = useMemo(() => aggregateNodePerformance(logs), [logs]);
  const intentTrend = useMemo(
    () =>
      history
        .slice(-12)
        .map((h) => {
          const createdAt = h?.createdAt ? new Date(h.createdAt) : null;
          const confidenceRaw = h?.truth?.confidence;
          const confidence = Number.isFinite(confidenceRaw)
            ? Math.max(0, Math.min(100, Math.round((confidenceRaw as number) * 100)))
            : 0;
          return {
            at: createdAt && !Number.isNaN(createdAt.getTime()) ? createdAt.toLocaleTimeString() : 'Unknown',
            confidence,
          };
        })
        .filter((point) => point.at !== 'Unknown'),
    [history]
  );
  const mismatchBlocks = useMemo(() => {
    if (!currentWorkflow) return [];
    return analyzeInlineValidation(currentWorkflow).suggestions
      .filter((s) => s.type === 'error' || s.safety === 'human-required')
      .slice(0, 6);
  }, [currentWorkflow]);

  const totalExecutions = analyticsData.reduce((sum, d) => sum + d.executions, 0);
  const totalSuccess = analyticsData.reduce((sum, d) => sum + d.success, 0);
  const totalFailed = analyticsData.reduce((sum, d) => sum + d.failed, 0);
  const avgDuration =
    analyticsData.length > 0
      ? (analyticsData.reduce((sum, d) => sum + d.avgDuration, 0) / analyticsData.length).toFixed(2)
      : '0';
  const successRate =
    totalExecutions > 0 ? ((totalSuccess / Math.max(totalSuccess + totalFailed, 1)) * 100).toFixed(1) : '0';

  const executionStats = useMemo(() => {
    const successPct = totalExecutions > 0 ? Math.round((totalSuccess / (totalSuccess + totalFailed || 1)) * 100) : 0;
    return [
      { name: 'Success', value: successPct, color: '#10b981' },
      { name: 'Failed', value: 100 - successPct, color: '#ef4444' },
    ];
  }, [totalSuccess, totalFailed, totalExecutions]);

  const handleExportReport = () => {
    const report = `WORKFLOW ANALYTICS\nTotal starts: ${totalExecutions}\nSuccess logs: ${totalSuccess}\nErrors: ${totalFailed}\n`;
    const blob = new Blob([report], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `workflow-analytics-${new Date().toISOString().split('T')[0]}.txt`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('Report exported');
  };

  if (logs.length === 0) {
    return (
      <Card className="p-8 text-center text-muted-foreground space-y-3">
        <p className="text-sm font-medium text-foreground">No execution data yet</p>
        <p className="text-xs">Run flows from the Mission map — stats populate from live logs.</p>
        <Button asChild size="sm" variant="outline">
          <Link to={APP_ROUTES.missionMap}>Open Mission map</Link>
        </Button>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground mb-2">Workflow Analytics</h2>
          <p className="text-muted-foreground text-sm">Derived from execution logs (last 7 days)</p>
        </div>
        <Button onClick={handleExportReport} size="sm">
          <Download className="w-4 h-4 mr-2" />
          Export
        </Button>
      </div>

      <div className="grid md:grid-cols-4 gap-4">
        <Card className="p-4 space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">Flow starts</p>
            <Zap className="w-5 h-5 text-blue-600" />
          </div>
          <p className="text-2xl font-bold">{totalExecutions}</p>
        </Card>
        <Card className="p-4 space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">Success logs</p>
            <TrendingUp className="w-5 h-5 text-green-600" />
          </div>
          <p className="text-2xl font-bold">{successRate}%</p>
          <Badge className="w-fit bg-green-500/10 text-green-700">{totalSuccess} entries</Badge>
        </Card>
        <Card className="p-4 space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">Errors</p>
            <TrendingDown className="w-5 h-5 text-red-600" />
          </div>
          <p className="text-2xl font-bold">{totalFailed}</p>
        </Card>
        <Card className="p-4 space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">Avg score</p>
            <Activity className="w-5 h-5 text-amber-600" />
          </div>
          <p className="text-2xl font-bold">{avgDuration}s</p>
        </Card>
      </div>

      <Tabs defaultValue="execution" className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="execution">Trend</TabsTrigger>
          <TabsTrigger value="success">Success rate</TabsTrigger>
          <TabsTrigger value="nodes">Step activity</TabsTrigger>
          <TabsTrigger value="truth">Intent match</TabsTrigger>
        </TabsList>

        <TabsContent value="execution">
          <Card className="p-6">
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={analyticsData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="executions" stroke="#3b82f6" name="Starts" />
                <Line type="monotone" dataKey="success" stroke="#10b981" name="Success logs" />
                <Line type="monotone" dataKey="failed" stroke="#ef4444" name="Errors" />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </TabsContent>

        <TabsContent value="success">
          <Card className="p-6">
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={executionStats} dataKey="value" nameKey="name" outerRadius={100} label>
                  {executionStats.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </TabsContent>

        <TabsContent value="nodes">
          <Card className="p-6 space-y-3">
            {nodePerformance.length === 0 ? (
              <p className="text-sm text-muted-foreground">Run flows to see step activity.</p>
            ) : (
              nodePerformance.map((node) => (
                <div key={node.name} className="p-3 rounded border border-border flex justify-between gap-2">
                  <span className="text-sm font-medium truncate">{node.name}</span>
                  <Badge variant="outline">{node.executions}×</Badge>
                </div>
              ))
            )}
          </Card>
        </TabsContent>

        <TabsContent value="truth">
          <Card className="p-6 space-y-4">
            <p className="text-sm font-medium text-foreground">Intent-match trend</p>
            {intentTrend.length === 0 ? (
              <p className="text-sm text-muted-foreground">No tracing history yet.</p>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={intentTrend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="at" />
                  <YAxis domain={[0, 100]} />
                  <Tooltip />
                  <Line type="monotone" dataKey="confidence" stroke="#22c55e" name="Confidence %" />
                </LineChart>
              </ResponsiveContainer>
            )}
            <div className="space-y-2">
              <p className="text-sm font-medium text-foreground">Top mismatch blocks</p>
              {mismatchBlocks.length === 0 ? (
                <p className="text-xs text-muted-foreground">No mismatch blocks detected.</p>
              ) : (
                mismatchBlocks.map((s) => (
                  <div key={s.id} className="rounded border border-border p-2 text-xs">
                    <p className="font-medium">{s.title}</p>
                    <p className="text-muted-foreground">{s.detail}</p>
                  </div>
                ))
              )}
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
